<template>
    <div v-if="fields.length" class="ticket-fields">
        <TransitionExpand>
            <div v-if="isExpanded" class="ticket-fields-rows" v-suppress>
                <Field
                    v-for="(field, fieldIndex) in fields"
                    :key="fieldIndex"
                    :label="field?.label"
                    :value="field?.value" />
            </div>
        </TransitionExpand>
        <ButtonLink :onClick="toggleExpand">
            {{ t({ key: isExpanded ? 'shared.show_less' : 'shared.show_more' }) }}
        </ButtonLink>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, PropType, ref } from 'vue';
    import {
        ActivityTicket,
        ActivityTicketPax,
        ActivityTicketSpecificField,
        BookingQuestionAnswerType,
        MandatoryFieldId,
    } from '@/api/Cart/cart.interfaces';
    import { t } from '@shared/helpers/translation';
    import { convertToDate, getFormattedDate } from '@shared/helpers/datetime/datetime';
    import { DateFormat } from '@shared/interfaces/dates';
    import { isNotNil } from '@shared/utils/value';
    import { getCapitalizedCase } from '@shared/utils/string';
    import TransitionExpand from '@ui/components/TransitionExpand/TransitionExpand.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import Field from './Field/Field.vue';

    interface FieldProps {
        label: string;
        value?: string | null;
    }

    export default defineComponent({
        name: 'Fields',
        methods: { t },
        components: { ButtonLink, TransitionExpand, Field },
        emits: ['expand'],
        props: {
            ticket: {
                type: Object as PropType<ActivityTicket>,
                required: true,
            },
        },
        setup(props, { emit }) {
            const isExpanded = ref(false);

            const toggleExpand = () => {
                isExpanded.value = !isExpanded.value;
                emit('expand');
            };

            const isMandatory = (pax: ActivityTicketPax, field: MandatoryFieldId): boolean =>
                pax.MandatoryFields.includes(field);

            const getField = (
                pax: ActivityTicketPax,
                id: MandatoryFieldId
            ): FieldProps | null => {
                if (!isMandatory(pax, id)) {
                    return null;
                }

                switch (id) {
                    case MandatoryFieldId.FIRST_NAME:
                        return {
                            label: t({ key: 'shared.first_name' }),
                            value: pax.FirstName,
                        };
                    case MandatoryFieldId.LAST_NAME:
                        return {
                            label: t({ key: 'shared.last_name' }),
                            value: pax.LastName,
                        };
                    case MandatoryFieldId.EMAIL:
                        return {
                            label: t({ key: 'checkout.email_label' }),
                            value: pax.Email,
                        };
                    case MandatoryFieldId.PHONE_NUMBER:
                        return {
                            label: t({ key: 'shared.phone_number' }),
                            value: pax.Phone,
                        };
                    case MandatoryFieldId.NATIONALITY:
                        return {
                            label: t({ key: 'shared.nationality' }),
                            value: pax.Nationality?.NationalityName,
                        };
                    case MandatoryFieldId.LANGUAGE:
                        return {
                            label: t({ key: 'shared.language' }),
                            value: pax.Language,
                        };
                    case MandatoryFieldId.DATE_OF_BIRTH:
                        return {
                            label: t({ key: 'shared.date_of_birth' }),
                            value: pax.DateOfBirth
                                ? getFormattedDate(
                                      convertToDate(pax.DateOfBirth),
                                      DateFormat.DateReverse
                                  )
                                : null,
                        };
                    case MandatoryFieldId.GENDER:
                        return {
                            label: t({ key: 'shared.gender' }),
                            value: pax.Gender
                                ? t({
                                      key: 'shared.genders.' + pax.Gender.toLowerCase(),
                                  })
                                : null,
                        };
                    case MandatoryFieldId.ADDRESS:
                        return {
                            label: t({ key: 'shared.address' }),
                            value: pax.Address,
                        };
                    case MandatoryFieldId.PASSPORT_ID:
                        return {
                            label: t({ key: 'shared.passport_number' }),
                            value: pax.PassportNumber,
                        };
                    case MandatoryFieldId.PASSPORT_EXPIRY:
                        return {
                            label: t({
                                key: 'shared.passport_expiration_date',
                            }),
                            value: pax.PassportExpirationDate
                                ? getFormattedDate(
                                      convertToDate(pax.PassportExpirationDate),
                                      DateFormat.DateReverse
                                  )
                                : null,
                        };
                    case MandatoryFieldId.PERSONAL_ID_NUMBER:
                        return {
                            label: t({ key: 'checkout.personal_id_number' }),
                            value: pax.PersonalIdNumber,
                        };
                    case MandatoryFieldId.ORGANIZATION:
                        return {
                            label: t({ key: 'shared.organization' }),
                            value: pax.Organization,
                        };
                    default:
                        return null;
                }
            };

            const getSelectMultipleFieldAnswers = (
                field: ActivityTicketSpecificField
            ): string => {
                const result: string[] = [];
                const answers = field.Answer;

                field.AnswerOptions.forEach((o) => {
                    const optionId = o.Id;

                    if (answers.includes(optionId)) {
                        result.push(o.Value);
                    }
                });

                return result.join(', ');
            };

            const getSelectFieldAnswer = (field: ActivityTicketSpecificField): string => {
                const [answer] = field.Answer;

                return field.AnswerOptions.filter((o) => o.Id === answer)[0]?.Value || answer;
            };

            const getTicketFieldValue = (field: ActivityTicketSpecificField) => {
                const [answer] = field.Answer;

                switch (field.ExpectedAnswerType) {
                    case BookingQuestionAnswerType.ShortText |
                        BookingQuestionAnswerType.LongText:
                        return answer;
                    case BookingQuestionAnswerType.Integer |
                        BookingQuestionAnswerType.Decimal:
                        return answer;
                    case BookingQuestionAnswerType.Boolean:
                        if (answer === 'true') {
                            return t({ key: 'shared.yes' });
                        } else if (answer === 'false') {
                            return t({ key: 'shared.no' });
                        } else {
                            return undefined;
                        }
                    case BookingQuestionAnswerType.Date:
                        if (isNotNil(answer)) {
                            return getFormattedDate(
                                convertToDate(answer),
                                DateFormat.DateReverse
                            );
                        } else {
                            return undefined;
                        }
                    case BookingQuestionAnswerType.DateAndTime:
                        if (isNotNil(answer)) {
                            return getFormattedDate(
                                convertToDate(answer),
                                DateFormat.FullDateTime
                            );
                        } else {
                            return undefined;
                        }
                    case BookingQuestionAnswerType.ListOfValues:
                        if (field.CanSelectMultipleOptions) {
                            return getSelectMultipleFieldAnswers(field);
                        } else {
                            return getSelectFieldAnswer(field);
                        }
                    case BookingQuestionAnswerType.ListOfValuesOrText:
                        return getSelectFieldAnswer(field);
                    default:
                        return answer;
                }
            };

            const fields = computed<FieldProps[]>(() => {
                const pax = props.ticket.Pax;
                const exclude: MandatoryFieldId[] = [
                    MandatoryFieldId.TITLE,
                    MandatoryFieldId.FIRST_NAME,
                    MandatoryFieldId.LAST_NAME,
                ];

                return [
                    ...Object.values(MandatoryFieldId)
                        .filter((x) => !exclude.includes(x))
                        .map((value) => getField(pax, MandatoryFieldId[value])),
                    ...pax.TicketFields.map((x) => ({
                        label: getCapitalizedCase(x.Question),
                        value: getTicketFieldValue(x),
                    })),
                ].filter(isNotNil);
            });

            return { toggleExpand, isExpanded, fields };
        },
    });
</script>

<style scoped lang="scss">
    @forward 'Fields';
</style>
